/**
 * PageLoader — Full-page loading transition
 * PageTransition — Wraps page content with loading → content crossfade
 *
 * Reusable across all pages for consistent loading UX.
 * Shows a centered animated loader with optional status text.
 *
 * @see Issue #612
 */
import { FeedSkeleton } from "./FeedSkeleton";

type SkeletonVariant = "card" | "list" | "detail" | "profile" | "chat" | "stat";

interface PageLoaderProps {
  /** Optional status text shown below the spinner */
  message?: string;
  /** Compact variant for inline sections (not full-page) */
  compact?: boolean;
}

export function PageLoader({ message = "加载中", compact = false }: PageLoaderProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center ${
        compact ? "py-12" : "min-h-[60vh]"
      } page-loader-enter`}
    >
      {/* Animated logo pulse */}
      <div className="relative mb-6">
        <div className="w-12 h-12 flex items-center justify-center animate-pulse">
          <svg viewBox="60 50 360 430" className="w-12 h-12"><rect x="80" y="72" width="232" height="232" rx="12" fill="#3b82f6" opacity=".55"/><circle cx="332" cy="212" r="128" fill="#8b5cf6" opacity=".55"/><polygon points="256,200 396,308 342,452 170,452 116,308" fill="#06b6d4" opacity=".55"/></svg>
        </div>
        {/* Orbiting dot */}
        <div className="absolute inset-0 animate-spin" style={{ animationDuration: "2s" }}>
          <div className="w-2 h-2 rounded-full bg-blue-400 absolute -top-1 left-1/2 -translate-x-1/2" />
        </div>
      </div>

      {/* Shimmer bar */}
      <div className="w-32 h-1 rounded-full bg-slate-100 overflow-hidden mb-3">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 animate-shimmer"
          style={{ width: "40%", animationDuration: "1.5s" }}
        />
      </div>

      {/* Status text */}
      <p className="text-sm text-slate-400 animate-pulse">{message}</p>
    </div>
  );
}

/**
 * PageTransition — Loading → Content crossfade wrapper.
 *
 * Modes:
 * 1. `skeleton` prop → renders skeleton while loading (recommended for feed/content)
 * 2. No skeleton → renders PageLoader spinner while loading (for full-page waits)
 *
 * Usage:
 *   <PageTransition loading={isLoading} message="加载 Club 数据...">
 *     <ClubContent />
 *   </PageTransition>
 *
 *   <PageTransition loading={isLoading} skeleton="profile">
 *     <ProfileContent />
 *   </PageTransition>
 */
export function PageTransition({
  loading,
  message,
  skeleton,
  skeletonCount,
  children,
}: {
  loading: boolean;
  message?: string;
  /** Skeleton variant to show during loading. If omitted, uses PageLoader spinner. */
  skeleton?: SkeletonVariant;
  skeletonCount?: number;
  children: React.ReactNode;
}) {
  if (loading) {
    if (skeleton) {
      return <FeedSkeleton variant={skeleton} count={skeletonCount} />;
    }
    return <PageLoader message={message} />;
  }
  return <div className="content-enter">{children}</div>;
}
